import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Typography
} from '@mui/material'
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { useAuth } from 'components/AuthProvider'
import Button from 'components/Button'
import { verifyingPath } from 'components/Routes'
import TextFieldCustom from 'components/TextFieldCustom'
import DefaultLayout from 'components/layout/DefaultLayout'
import { ETHNICITIES } from 'constants/index'
import dayjs from 'dayjs'
import { FormikErrors, useFormik } from 'formik'
import {
  useIdentityVerification,
  useMutateRequestIdentityVerification
} from 'hooks/useCompliance'
import { useUserProfile, useMutateUserProfile } from 'hooks/useUserProfile'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { API_DATE_FORMAT, UI_DATE_FORMAT, formatDate } from 'utils/util'
import * as Yup from 'yup'
import { formFieldData } from '../formFieldData'
import formValidation from '../formValidation'
// Local types for verification form data (not persisted via REST profile update)
interface UpdateProfileForOnboardingInput {
  firstName: string
  middleName?: string
  lastName: string
  dateOfBirth: string
  addressLine1: string
  addressLine2?: string
  suburb: string
  city: string
  postcode: string
  verificationMethod?: string
  ethnicity?: string
  verificationData?: {
    driversLicenceNumber?: string
    driversLicenceVersion?: string
    passportExpiry?: string
    passportNumber?: string
  }
}

type Maybe<T> = T | null | undefined

interface FormValues {
  firstName: string
  middleName: string
  lastName: string
  dateOfBirth: string
  addressLine1: string
  addressLine2: string
  suburb: string
  city: string
  postcode: string
  driversLicenceNumber?: string
  driversLicenceVersion?: string
  passportExpiry?: string
  passportNumber?: string
  verificationMethod?: 'passport' | 'drivers_licence' | 'other'
  ethnicity?: string
}

interface CopiedFormFieldsState {
  firstName: boolean
  middleName: boolean
  lastName: boolean
  dateOfBirth: boolean
  addressLine1: boolean
  addressLine2: boolean
  suburb: boolean
  city: boolean
  postcode: boolean
  driversLicenceNumber?: boolean
  driversLicenceVersion?: boolean
  passportExpiry?: boolean
  passportNumber?: boolean
  verificationMethod?: boolean
  ethnicity?: boolean
}

const initialValues: FormValues = {
  firstName: '',
  middleName: '',
  lastName: '',
  dateOfBirth: '',
  addressLine1: '',
  addressLine2: '',
  suburb: '',
  city: '',
  postcode: '',
  driversLicenceNumber: '',
  driversLicenceVersion: '',
  passportExpiry: '',
  passportNumber: '',
  verificationMethod: 'drivers_licence',
  ethnicity: ''
}

const INITIAL_COPIED_FORM_FIELDS = {
  firstName: false,
  middleName: false,
  lastName: false,
  dateOfBirth: false,
  addressLine1: false,
  addressLine2: false,
  suburb: false,
  city: false,
  postcode: false,
  driversLicenceNumber: false,
  driversLicenceVersion: false,
  passportExpiry: false,
  passportNumber: false,
  verificationMethod: false,
  ethnicity: false
}

const validationSchema = Yup.object().shape({
  firstName: formValidation.firstName,
  middleName: formValidation.middleName,
  lastName: formValidation.lastName,
  dateOfBirth: formValidation.dateOfBirth,
  addressLine1: formValidation.addressLine1,
  addressLine2: formValidation.addressLine2,
  suburb: formValidation.suburb,
  city: formValidation.city,
  postcode: formValidation.postcode,
  driversLicenceNumber: formValidation.driversLicenceNumber,
  driversLicenceVersion: formValidation.driversLicenceVersion,
  passportExpiry: formValidation.passportExpiry,
  passportNumber: formValidation.passportNumber,
  ethnicity: formValidation.ethnicity
})

export default function VerificationError(): JSX.Element {
  const { setToast } = useAuth()
  const navigate = useNavigate()

  const [copiedFormFields, setCopiedFormFields] =
    useState<CopiedFormFieldsState>(INITIAL_COPIED_FORM_FIELDS)

  const {
    handleSubmit,
    handleBlur,
    handleChange,
    setFieldValue,
    setValues,
    setErrors,
    errors,
    values,
    dirty,
    isValid
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values: FormValues) => {
      const {
        driversLicenceNumber,
        driversLicenceVersion,
        passportExpiry,
        passportNumber,
        dateOfBirth,
        ...rest
      } = values

      const input: UpdateProfileForOnboardingInput = {
        ...rest,
        dateOfBirth: formatDate({
          date: dateOfBirth,
          inputFormat: UI_DATE_FORMAT,
          outputFormat: API_DATE_FORMAT
        }),
        verificationData:
          values.verificationMethod === 'drivers_licence'
            ? {
                driversLicenceNumber,
                driversLicenceVersion
              }
            : {
                passportExpiry,
                passportNumber
              }
      }
      // Update basic profile fields via REST, then request identity verification
      updateProfile({
        first_name: input.firstName,
        last_name: input.lastName,
        address_line1: input.addressLine1,
        address_line2: input.addressLine2,
        suburb: input.suburb,
        city: input.city,
        postcode: input.postcode
      })
    }
  })

  const { data: statusData, isLoading: statusLoading } =
    useIdentityVerification()

  const failureReason = statusData?.failure_reason

  async function setFormValue(
    profileData: {
      first_name?: string | null
      last_name?: string | null
      address_line1?: string | null
      address_line2?: string | null
      suburb?: string | null
      city?: string | null
      postcode?: string | null
      date_of_birth?: string | null
    },
    formErrors: FormikErrors<FormValues>
  ): Promise<Partial<FormValues>> {
    const values: Partial<FormValues> = {}

    if (profileData) {
      const fieldMap: Record<string, keyof FormValues> = {
        first_name: 'firstName',
        last_name: 'lastName',
        address_line1: 'addressLine1',
        address_line2: 'addressLine2',
        suburb: 'suburb',
        city: 'city',
        postcode: 'postcode'
      }

      for (const [apiKey, formKey] of Object.entries(fieldMap)) {
        const apiValue = profileData[apiKey as keyof typeof profileData]
        if (!formErrors[formKey] && apiValue) {
          if (formKey === 'dateOfBirth') {
            values[formKey] = formatDate({
              date: dayjs(apiValue).format(UI_DATE_FORMAT),
              inputFormat: UI_DATE_FORMAT,
              outputFormat: UI_DATE_FORMAT
            })
          } else {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ;(values as any)[formKey] = apiValue ?? undefined
          }
        }
      }
    }

    return values
  }

  const { data: profileData, isLoading: profileLoading } = useUserProfile()

  const {
    first_name: firstName,
    last_name: lastName,
    address_line1: addressLine1,
    address_line2: addressLine2,
    city,
    date_of_birth: dateOfBirth,
    postcode,
    suburb
  } = profileData ?? {}

  useEffect(() => {
    ;(async () => {
      const formErrors: FormikErrors<FormValues> = {}

      if (failureReason) {
        // REST API provides a single failure_reason string rather than structured failure data
        // Mark common fields as potentially errored when verification has failed
        formErrors.firstName = failureReason
          ? 'Please verify your first name'
          : ''
        formErrors.lastName = failureReason
          ? 'Please verify your last name'
          : ''
        formErrors.dateOfBirth = failureReason
          ? 'Please verify your date of birth'
          : ''
        formErrors.addressLine1 = failureReason
          ? 'Please verify your address'
          : ''
      }

      if (profileData && !statusLoading) {
        const values = await setFormValue(profileData, formErrors)
        setValues({ ...initialValues, ...(values as FormValues) })
        setErrors(formErrors || {})
      }
    })()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData, statusLoading, failureReason])

  const {
    mutateAsync: requestIdentityVerification,
    isLoading: requestVerificationLoading
  } = useMutateRequestIdentityVerification()

  const {
    mutateAsync: updateProfileMutation,
    isLoading: updateProfileLoading
  } = useMutateUserProfile()

  async function updateProfile(
    profileUpdate: Parameters<typeof updateProfileMutation>[0]
  ) {
    try {
      await updateProfileMutation(profileUpdate)
      await requestIdentityVerification({
        first_name: values.firstName,
        last_name: values.lastName,
        date_of_birth:
          formatDate({
            date: values.dateOfBirth,
            inputFormat: UI_DATE_FORMAT,
            outputFormat: API_DATE_FORMAT
          }) ?? '',
        address: `${values.addressLine1} ${values.addressLine2 ?? ''}`.trim(),
        city: values.city,
        postcode: values.postcode
      })
      navigate(verifyingPath())
    } catch (error) {
      setToast({
        open: true,
        message: error instanceof Error ? error.message : 'An error occurred',
        type: 'error',
        duration: 3000
      })
    }
  }

  const submitDisabled =
    !dirty || !isValid || updateProfileLoading || requestVerificationLoading

  function renderFailureDetails(): string | null {
    return failureReason ?? null
  }

  const copyToFormField =
    (name: keyof FormValues, value?: Maybe<string> | undefined) => () => {
      if (value) {
        setFieldValue(name, value)
        setCopiedFormFields(prev => ({
          ...prev,
          [name]: true
        }))
      }
    }

  function renderCopyLink(
    name: keyof FormValues,
    value?: Maybe<string> | undefined
  ) {
    if (!value) return

    return copiedFormFields[name] ? (
      <Typography sx={{ mt: 1.5 }}>You wrote {value}</Typography>
    ) : (
      <Link
        color='textPrimary'
        component='button'
        onClick={copyToFormField(name, value)}
        type='button'
        sx={{ mt: 1.5 }}
      >
        You wrote {value}
      </Link>
    )
  }

  function checkError(field: keyof FormValues): boolean {
    return !!errors[field]
  }

  function getHelperText(field: keyof FormValues): string {
    return checkError(field) ? (errors[field] as string) : ''
  }

  return (
    <DefaultLayout
      backgroundColor='white'
      heading='Uh oh, was there a typo?'
      loading={statusLoading || profileLoading}
    >
      <form noValidate onSubmit={handleSubmit}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Typography variant='body1'>
              You need to fill in your details exactly as they appear on your
              driver's licence, even if that's not what you usually use. You can
              leave the optional fields empty.
            </Typography>
          </Grid>

          {!!renderFailureDetails() && (
            <Grid item xs={12}>
              <Typography variant='body1'>
                We are having trouble verifying your details.{' '}
                {renderFailureDetails()}
              </Typography>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextFieldCustom
              {...formFieldData.firstName}
              error={checkError('firstName')}
              helperText={getHelperText('firstName')}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.firstName}
            />
            {checkError('firstName') && renderCopyLink('firstName', firstName)}
          </Grid>

          <Grid item xs={12}>
            <TextFieldCustom
              {...formFieldData.middleName}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.middleName}
            />
          </Grid>

          <Grid item xs={12}>
            <TextFieldCustom
              {...formFieldData.lastName}
              error={checkError('lastName')}
              helperText={getHelperText('lastName')}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.lastName}
            />
            {checkError('lastName') && renderCopyLink('lastName', lastName)}
          </Grid>

          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                format={UI_DATE_FORMAT}
                value={dayjs(
                  checkError('dateOfBirth') ? '' : values.dateOfBirth,
                  UI_DATE_FORMAT
                )}
                label='Date of birth'
                onChange={date => {
                  const formattedDate = date
                    ? formatDate({
                        date: dayjs(date).format(UI_DATE_FORMAT),
                        inputFormat: UI_DATE_FORMAT,
                        outputFormat: UI_DATE_FORMAT
                      })
                    : ''
                  setFieldValue('dateOfBirth', formattedDate)
                }}
                slotProps={{
                  textField: {
                    onBlur: handleBlur,
                    name: 'dateOfBirth',
                    required: true,
                    fullWidth: true,
                    error: checkError('dateOfBirth'),
                    helperText: getHelperText('dateOfBirth'),
                    value: values.dateOfBirth
                  }
                }}
              />
            </LocalizationProvider>
            {checkError('dateOfBirth') &&
              renderCopyLink(
                'dateOfBirth',
                dateOfBirth
                  ? formatDate({
                      date: dateOfBirth,
                      inputFormat: API_DATE_FORMAT,
                      outputFormat: UI_DATE_FORMAT
                    })
                  : undefined
              )}
          </Grid>

          <Grid item xs={12}>
            <TextFieldCustom
              {...formFieldData.addressLine1}
              error={checkError('addressLine1')}
              helperText={getHelperText('addressLine1')}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.addressLine1}
            />
            {checkError('addressLine1') &&
              renderCopyLink('addressLine1', addressLine1)}
          </Grid>

          <Grid item xs={12}>
            <TextFieldCustom
              {...formFieldData.addressLine2}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.addressLine2}
            />
            {checkError('addressLine2') &&
              renderCopyLink('addressLine2', addressLine2)}
          </Grid>

          <Grid item xs={12}>
            <TextFieldCustom
              {...formFieldData.suburb}
              error={checkError('suburb')}
              helperText={getHelperText('suburb')}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.suburb}
            />
            {checkError('suburb') && renderCopyLink('suburb', suburb)}
          </Grid>

          <Grid item xs={12} sm={8}>
            <TextFieldCustom
              {...formFieldData.city}
              error={checkError('city')}
              helperText={getHelperText('city')}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.city}
            />
            {checkError('city') && renderCopyLink('city', city)}
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextFieldCustom
              {...formFieldData.postcode}
              error={checkError('postcode')}
              helperText={getHelperText('postcode')}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.postcode}
            />
            {checkError('postcode') && renderCopyLink('postcode', postcode)}
          </Grid>

          {values.verificationMethod === 'drivers_licence' ? (
            <>
              <Grid item xs={12}>
                <TextFieldCustom
                  {...formFieldData.driversLicenceNumber}
                  error={checkError('driversLicenceNumber')}
                  helperText={getHelperText('driversLicenceNumber')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.driversLicenceNumber}
                />
              </Grid>

              <Grid item xs={12}>
                <TextFieldCustom
                  {...formFieldData.driversLicenceVersion}
                  error={checkError('driversLicenceVersion')}
                  helperText={getHelperText('driversLicenceVersion')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.driversLicenceVersion}
                />
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12}>
                <TextFieldCustom
                  {...formFieldData.passportNumber}
                  error={checkError('passportNumber')}
                  helperText={getHelperText('passportNumber')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.passportNumber}
                />
              </Grid>

              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    format={API_DATE_FORMAT}
                    value={dayjs(
                      checkError('passportExpiry') ? '' : values.passportExpiry,
                      API_DATE_FORMAT
                    )}
                    label='Passport expiry'
                    onChange={date => {
                      const formattedDate = date
                        ? formatDate({
                            date: dayjs(date).format(API_DATE_FORMAT),
                            inputFormat: API_DATE_FORMAT,
                            outputFormat: API_DATE_FORMAT
                          })
                        : ''
                      setFieldValue('passportExpiry', formattedDate)
                    }}
                    slotProps={{
                      textField: {
                        onBlur: handleBlur,
                        name: 'passportExpiry',
                        required: true,
                        fullWidth: true,
                        error: checkError('passportExpiry'),
                        helperText: getHelperText('passportExpiry'),
                        value: values.passportExpiry
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel
                id={formFieldData.ethnicity.name}
                error={checkError('ethnicity')}
              >
                {formFieldData.ethnicity.label}
              </InputLabel>
              <Select
                label=''
                name='ethnicity'
                value={values.ethnicity}
                onChange={handleChange}
                onBlur={handleBlur}
                onClickCapture={handleBlur}
                required
                error={checkError('ethnicity')}
              >
                {ETHNICITIES.map(ethnicity => (
                  <MenuItem key={ethnicity} value={ethnicity}>
                    {ethnicity}
                  </MenuItem>
                ))}
              </Select>
              {errors.ethnicity && (
                <FormHelperText error>{errors.ethnicity}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} mb='2rem'>
            <Button
              color='blue'
              disabled={submitDisabled}
              fullWidth
              type='submit'
            >
              Try again
            </Button>
          </Grid>
        </Grid>
      </form>
    </DefaultLayout>
  )
}
